import { API } from '@/utils/API';
import { useState, useCallback, useEffect, useRef } from 'react';

export interface Role {
  name: string;
  allowed_resources: (string | null)[];
}

interface UseFetchRolesResult {
  fetchRoles: (options?: RequestInit) => Promise<void>;
  clearError: () => void;
  roles: Record<string, Role>;
  isLoading: boolean;
  error: string | null;
}

export const useFetchRoles = (): UseFetchRolesResult => {
  const [roles, setRoles] = useState<Record<string, Role>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchRoles = useCallback(async (options: RequestInit = {}) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.admin.fetch('/roles', {
        signal: newAbortController.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        ...options,
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // Use default error message if parsing fails
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Handle different response formats
      const rolesData: Record<string, Role> =
        data.roles || data.data || data || {};
      setRoles(rolesData);
    } catch (err) {
      // Don't update state for aborted requests
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch roles';
      console.error('Error fetching roles:', err);
      setError(errorMessage);
      setRoles({});
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup: abort any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    fetchRoles,
    clearError,
    roles,
    isLoading,
    error,
  };
};
